import { Flex, Grid } from '@chakra-ui/react';
import { Temporal } from 'temporal-polyfill';
import { ITimeZone } from '../../types/ITimeZone';
import DayHeader from './components/day-header/day-header.view';
import TodayFloatingButton from './components/today-floating-button/today-floating-button';
import { useWeekCalendarHeaderController } from './week-calendar-header.controller';

interface IProps {
	/**
	 * First day of the header
	 */
	from: Temporal.PlainDate;
	/**
	 * Last day visible in the header
	 */
	to: Temporal.PlainDate;
	/**
	 * The day where the user lives.
	 * Do not confuse with currentDay.
	 */
	today: Temporal.PlainDate;
	/**
	 * The active day in the calendar.
	 * Do not confuse with today.
	 */
	currentDay: Temporal.PlainDate;
	/**
	 * TimeZone used in the header
	 */
	timeZone: ITimeZone;
	/**
	 * Function handler when the user clicks on a day in the header.
	 */
	onDayClick?: (day: Temporal.PlainDate, index: number) => void;
	/**
	 * Function handler when the user swipes to between weeks.
	 */
	onWeekChange?: (firstDay: Temporal.PlainDate) => void;
	/**
	 * Function handler executed when the user clicks on the "today" button.
	 */
	onTodayClick: () => void;
	showShadow: boolean;
}

export function WeekCalendarHeader(props: IProps) {
	const { ref, prevWeekDays, days, nextWeekDays } = useWeekCalendarHeaderController(props);

	return (
		<Grid
			as="nav"
			id="calendar-header-wrapper"
			data-testid="calendar-header"
			gridTemplateColumns={{ base: '1fr' }}
			position="relative"
			pl={{ base: 0, lg: 12 }}
			zIndex={2}
			shadow={props.showShadow ? 'sm' : 'none'}
		>
			<Flex
				w="full"
				overflow="auto"
				scrollSnapStop="always"
				scrollSnapType="x mandatory"
				ref={ref}
				css={{
					'&::-webkit-scrollbar': {
						display: 'none',
					},
					scrollbarColor: 'transparent',
				}}
				transition="opacity .2s"
			>
				<Grid
					id="calendar-header-0"
					gridTemplateColumns={`repeat(${days.length}, 1fr)`}
					flex="1 0 auto"
					gap={1}
					w="full"
					borderLeftWidth={1}
					borderLeftColor="transparent"
					scrollSnapAlign="center"
				>
					{prevWeekDays.map((d, index) => {
						return (
							<Grid
								key={d.toString()}
								gridTemplateRows="1fr"
								gap={1}
								p={2}
								pb={4}
								textAlign="center"
								cursor="pointer"
								onClick={() => {
									if (props.onDayClick) {
										props.onDayClick(d, index);
									}
								}}
							>
								<DayHeader day={d} timeZone={props.timeZone} currentDay={props.currentDay} today={props.today} />
							</Grid>
						);
					})}
				</Grid>
				<Grid
					id="calendar-header-1"
					gridTemplateColumns={`repeat(${days.length}, 1fr)`}
					flex="1 0 auto"
					gap={1}
					w="full"
					borderLeftWidth={1}
					borderLeftColor="transparent"
					scrollSnapAlign="center"
				>
					{days.map((d, index) => {
						return (
							<Grid
								key={d.toString()}
								gridTemplateRows="1fr"
								gap={1}
								p={2}
								pb={4}
								textAlign="center"
								cursor="pointer"
								onClick={() => {
									if (props.onDayClick) {
										props.onDayClick(d, index);
									}
								}}
							>
								<DayHeader day={d} timeZone={props.timeZone} currentDay={props.currentDay} today={props.today} />
							</Grid>
						);
					})}
				</Grid>
				<Grid
					id="calendar-header-2"
					gridTemplateColumns={`repeat(${days.length}, 1fr)`}
					flex="1 0 auto"
					gap={1}
					w="full"
					borderLeftWidth={1}
					borderLeftColor="transparent"
					scrollSnapAlign="center"
				>
					{nextWeekDays.map((d, index) => {
						return (
							<Grid
								key={d.toString()}
								gap={1}
								textAlign="center"
								p={2}
								pb={4}
								cursor="pointer"
								onClick={() => {
									if (props.onDayClick) {
										props.onDayClick(d, index);
									}
								}}
							>
								<DayHeader day={d} timeZone={props.timeZone} currentDay={props.currentDay} today={props.today} />
							</Grid>
						);
					})}
				</Grid>
			</Flex>
			<TodayFloatingButton onTodayClick={props.onTodayClick} days={days} today={props.today} />
		</Grid>
	);
}
